import { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sidebar, MobileMenuButton, RefreshButton } from '../components/Sidebar';
import { BottomNav } from '../components/BottomNav';
import { UnprocessedEmailsAlert } from '../components/dashboard/UnprocessedEmailsAlert';
import { sortData } from '../components/SortableHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, CheckCircle, Trash2, Loader2, AlertTriangle, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { toastDeletedWithUndo } from '../lib/undoDelete';

// Import modular components
import { 
    DateFilter, 
    SingleDayFilter,
    QuickRangeButtons,
    UserFilter, 
    FilterActions,
    StatsGrid,
    InvoiceSummary,
    LoadMoreButton,
    EmptyState,
    LoadingSpinner,
    SimpleInvoiceTable,
    formatCurrency,
    VerificationStatsPanel,
    PendingInvoicesAlert,
    ParsingStatsPanel,
    PerformanceHistoryPanel
} from '../components/dashboard';

const API = `/api`;

// Component for comparing two invoices side by side
const InvoiceCompareCard = ({ invoice, label, labelColor, getAuthHeaders }) => {
    const [imageData, setImageData] = useState(null);
    const [loadingImage, setLoadingImage] = useState(false);

    const loadImage = async () => {
        if (imageData || !invoice?.id) return;
        setLoadingImage(true);
        try {
            const response = await axios.get(`${API}/invoices/${invoice.id}/image`, {
                headers: getAuthHeaders()
            });
            setImageData(response.data.image_data);
        } catch (error) {
            console.error('Error loading image:', error);
        } finally {
            setLoadingImage(false);
        }
    };

    useEffect(() => {
        if (invoice?.id) {
            loadImage();
        }
    }, [invoice?.id]);

    if (!invoice) {
        return (
            <div className="flex-1 bg-slate-100 rounded-lg p-4 text-center text-slate-500">
                Factura original no encontrada
            </div>
        );
    }

    return (
        <div className="flex-1 bg-white rounded-lg border border-slate-200 p-4">
            <div className={`text-xs font-semibold ${labelColor} mb-2 uppercase`}>{label}</div>
            <div className="space-y-2">
                <div>
                    <span className="text-sm text-slate-500">Nombre:</span>
                    <p className="font-semibold text-slate-800">{invoice.name}</p>
                </div>
                <div>
                    <span className="text-sm text-slate-500">Monto:</span>
                    <p className="text-xl font-bold text-sky-600">{formatCurrency(invoice.amount)}</p>
                </div>
                <div>
                    <span className="text-sm text-slate-500">Usuario:</span>
                    <p className="text-slate-700">{invoice.user_name}</p>
                </div>
                {/* Bank verification info */}
                <div>
                    <span className="text-sm text-slate-500">Banco/Verificación:</span>
                    {invoice.verification_details?.bank ? (
                        <p className="text-slate-700">
                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-sm font-medium">
                                ✓ {invoice.verification_details.bank}
                            </span>
                            {invoice.verification_details.sender_name && (
                                <span className="text-slate-500 text-xs ml-2">
                                    Remitente: {invoice.verification_details.sender_name}
                                </span>
                            )}
                            {invoice.verification_details.stripe_fee_type && (
                                <span className={`text-xs ml-2 px-1.5 py-0.5 rounded-full font-medium ${
                                    invoice.verification_details.stripe_fee_type === 'internacional'
                                        ? 'bg-purple-100 text-purple-700'
                                        : invoice.verification_details.stripe_fee_type === 'doméstico'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-gray-100 text-gray-700'
                                }`}
                                title={`Neto subido: $${invoice.verification_details.stripe_net_uploaded || '?'} | Email bruto: $${invoice.verification_details.stripe_email_gross || '?'} | Cálculo dom: $${invoice.verification_details.stripe_domestic_calc || '?'} | Cálculo intl: $${invoice.verification_details.stripe_intl_calc || '?'}`}
                                >
                                    {invoice.verification_details.stripe_fee_type === 'internacional' ? 'Intl' : 'Dom'} {invoice.verification_details.stripe_fee_rate}
                                </span>
                            )}
                        </p>
                    ) : invoice.confirmed ? (
                        <p className="text-slate-600 text-sm">Confirmada manualmente</p>
                    ) : (
                        <p className="text-slate-400 text-sm">No verificada</p>
                    )}
                </div>
                {/* Status */}
                <div>
                    <span className="text-sm text-slate-500">Estado:</span>
                    <p>
                        {invoice.confirmed ? (
                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-sm">
                                ✓ Confirmada
                            </span>
                        ) : invoice.pending_review ? (
                            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm">
                                ⚠ Pendiente revisión
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-sm">
                                Pendiente
                            </span>
                        )}
                    </p>
                </div>
                <div>
                    <span className="text-sm text-slate-500">Descripción:</span>
                    <p className="text-slate-600 text-sm">{invoice.description || '-'}</p>
                </div>
                <div>
                    <span className="text-sm text-slate-500">Fecha:</span>
                    <p className="text-slate-600 text-sm">
                        {invoice.created_at ? new Date(invoice.created_at).toLocaleString('es-ES') : '-'}
                    </p>
                </div>
                {/* Image */}
                <div className="mt-3">
                    <span className="text-sm text-slate-500">Imagen:</span>
                    {loadingImage ? (
                        <div className="mt-2 flex items-center justify-center h-32 bg-slate-50 rounded-lg">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                    ) : imageData ? (
                        <img 
                            src={`data:image/jpeg;base64,${imageData}`}
                            alt={invoice.name}
                            className="mt-2 rounded-lg border border-slate-200 max-h-48 w-full object-contain bg-slate-50"
                        />
                    ) : (
                        <p className="text-slate-400 text-sm mt-1">Sin imagen</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// Component for pending review items with expandable comparison
const PendingReviewItem = ({ invoice, onApprove, onDelete, formatCurrency, getAuthHeaders }) => {
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [originalInvoice, setOriginalInvoice] = useState(null);
    const [loadingOriginal, setLoadingOriginal] = useState(false);

    const loadOriginalInvoice = async () => {
        if (!invoice.duplicate_of || originalInvoice) return;
        setLoadingOriginal(true);
        try {
            const response = await axios.get(`${API}/invoices`, {
                headers: getAuthHeaders()
            });
            const original = response.data.find(inv => inv.id === invoice.duplicate_of);
            setOriginalInvoice(original || null);
        } catch (error) {
            console.error('Error loading original invoice:', error);
        } finally {
            setLoadingOriginal(false);
        }
    };

    const handleExpand = () => {
        if (!expanded && !originalInvoice) {
            loadOriginalInvoice();
        }
        setExpanded(!expanded);
    };

    const handleApprove = async () => {
        setLoading(true);
        await onApprove(invoice.id);
        setLoading(false);
    };

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar esta factura del sistema?')) return;
        setLoading(true);
        await onDelete(invoice.id);
        setLoading(false);
    };

    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="font-semibold text-slate-800">{invoice.name}</span>
                            <span className="text-lg font-bold text-amber-700">{formatCurrency(invoice.amount)}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                            Subida por: <span className="font-medium">{invoice.user_name}</span>
                        </p>
                        {invoice.duplicate_reason && (
                            <p className="text-sm text-amber-700 mt-1 bg-amber-100 px-2 py-1 rounded inline-block">
                                {invoice.duplicate_reason}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleExpand}
                            className="text-slate-600 border-slate-300"
                        >
                            <Eye className="w-4 h-4 mr-1" />
                            Comparar
                            {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleApprove}
                            disabled={loading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <><CheckCircle className="w-4 h-4 mr-1" /> Aprobar</>
                            )}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDelete}
                            disabled={loading}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Expanded comparison section */}
            {expanded && (
                <div className="border-t border-amber-200 bg-white p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Comparación de Facturas</h4>
                    {loadingOriginal ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                            <span className="ml-2 text-slate-500">Cargando factura original...</span>
                        </div>
                    ) : (
                        <div className="flex gap-4">
                            <InvoiceCompareCard 
                                invoice={originalInvoice}
                                label="Factura Original"
                                labelColor="text-green-600"
                                getAuthHeaders={getAuthHeaders}
                            />
                            <InvoiceCompareCard 
                                invoice={invoice}
                                label="Nueva Factura (Posible Duplicado)"
                                labelColor="text-amber-600"
                                getAuthHeaders={getAuthHeaders}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const DashboardHome = () => {
    const { user, getAuthHeaders } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        total_confirmed: 0,
        total_pending: 0,
        count_confirmed: 0,
        count_pending: 0
    });
    const [todayInvoices, setTodayInvoices] = useState([]);
    const [pendingReviewInvoices, setPendingReviewInvoices] = useState([]);
    const [userPendingInvoices, setUserPendingInvoices] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filter states
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('all');
    
    // Sorting states
    const [sortKey, setSortKey] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    
    // Pagination state
    const [visibleCount, setVisibleCount] = useState(10);
    const [listSummary, setListSummary] = useState(null);
    const [listQuery, setListQuery] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);
    const ITEMS_PER_PAGE = 10;

    const getTodayDate = () => {
        const miamiDate = new Date().toLocaleString('en-US', { 
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        const [month, day, year] = miamiDate.split('/');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const todayDate = getTodayDate();
            const requests = [
                // Get totals filtered by today's date
                axios.get(`${API}/invoices/totals?date_from=${todayDate}&date_to=${todayDate}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/invoices?date_from=${todayDate}&date_to=${todayDate}&limit=200`, { headers: getAuthHeaders() })
            ];
            
            if (user?.role === 'admin' || user?.role === 'supervisor') {
                requests.push(axios.get(`${API}/users`, { headers: getAuthHeaders() }));
                requests.push(axios.get(`${API}/invoices/pending-review`, { headers: getAuthHeaders() }));
            }
            
            // For regular users, get ALL their pending invoices (not just today's)
            if (user?.role === 'usuario') {
                requests.push(axios.get(`${API}/invoices?limit=100`, { headers: getAuthHeaders() }));
            }
            
            const responses = await Promise.all(requests);
            setStats(responses[0].data);
            setTodayInvoices(responses[1].data);
            const todayQuery = `date_from=${todayDate}&date_to=${todayDate}`;
            setListQuery(todayQuery);
            axios.get(`${API}/invoices/summary?${todayQuery}`, { headers: getAuthHeaders() }).then(r => setListSummary(r.data)).catch(() => setListSummary(null));
            
            if (user?.role === 'admin' || user?.role === 'supervisor') {
                if (responses[2]) {
                    setUsers(responses[2].data.filter(u => u.role === 'usuario'));
                }
                if (responses[3]) {
                    setPendingReviewInvoices(responses[3].data);
                }
            }
            
            // Set pending invoices for regular users
            if (user?.role === 'usuario') {
                const pendingIndex = (user?.role === 'admin' || user?.role === 'supervisor') ? 4 : 2;
                if (responses[pendingIndex]) {
                    const pendingForUser = responses[pendingIndex].data.filter(inv => !inv.confirmed);
                    setUserPendingInvoices(pendingForUser);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchFilteredData = async (overrides = {}) => {
        setLoading(true);
        try {
            const todayDate = getTodayDate();
            const from = overrides.dateFrom ?? dateFrom;
            const to = overrides.dateTo ?? dateTo;
            const hasDateFilter = from || to;
            const hasUserFilter = selectedUserId && selectedUserId !== 'all';
            
            const params = new URLSearchParams();
            
            if (hasDateFilter) {
                if (from) params.append('date_from', format(from, 'yyyy-MM-dd'));
                if (to) params.append('date_to', format(to, 'yyyy-MM-dd'));
            } else {
                params.append('date_from', todayDate);
                params.append('date_to', todayDate);
            }
            
            if (hasUserFilter) params.append('user_id', selectedUserId);
            const baseQuery = params.toString();
            params.append('limit', '200');
            
            const queryString = params.toString();
            
            const [totalsRes, invoicesRes, summaryRes] = await Promise.all([
                axios.get(`${API}/invoices/totals?${queryString}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/invoices?${queryString}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/invoices/summary?${baseQuery}`, { headers: getAuthHeaders() }).catch(() => null),
            ]);
            
            setStats(totalsRes.data);
            setTodayInvoices(invoicesRes.data);
            setListSummary(summaryRes?.data || null);
            setListQuery(baseQuery);
            
            // Update pending invoices for regular users
            if (user?.role === 'usuario') {
                const pendingForUser = invoicesRes.data.filter(inv => !inv.confirmed);
                setUserPendingInvoices(pendingForUser);
            }
        } catch (error) {
            console.error('Error fetching filtered data:', error);
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setDateFrom(null);
        setDateTo(null);
        setSelectedUserId('all');
        setVisibleCount(10);
        fetchInitialData();
    };

    const selectRange = (from, to) => {
        setDateFrom(from);
        setDateTo(to);
        setVisibleCount(10);
        fetchFilteredData({ dateFrom: from, dateTo: to });
    };
    const selectSingleDay = (day) => selectRange(day, day);

    const handleApproveReview = async (invoiceId) => {
        try {
            await axios.patch(`${API}/invoices/${invoiceId}/approve-review`, {}, {
                headers: getAuthHeaders()
            });
            toast.success('Factura aprobada correctamente');
            setPendingReviewInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
            fetchInitialData();
        } catch (error) {
            console.error('Error approving invoice:', error);
            toast.error('Error al aprobar la factura');
        }
    };

    const handleDeletePendingReview = async (invoiceId) => {
        try {
            await axios.delete(`${API}/invoices/${invoiceId}`, {
                headers: getAuthHeaders()
            });
            toastDeletedWithUndo({ invoiceId, headers: getAuthHeaders(), onRestored: () => fetchInitialData() });
            setPendingReviewInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
        } catch (error) {
            console.error('Error deleting invoice:', error);
            toast.error('Error al eliminar la factura');
        }
    };

    const hasActiveFilters = dateFrom || dateTo || (selectedUserId && selectedUserId !== 'all');
    const canFilterByUser = user?.role === 'admin' || user?.role === 'supervisor';

    const handleSort = (key, direction) => {
        setSortKey(key);
        setSortDirection(direction);
    };
    
    const sortedInvoices = useMemo(() => sortData(todayInvoices, sortKey, sortDirection), [todayInvoices, sortKey, sortDirection]);
    const paginatedInvoices = useMemo(() => sortedInvoices.slice(0, visibleCount), [sortedInvoices, visibleCount]);
    const hasMoreInvoices = sortedInvoices.length > visibleCount || todayInvoices.length < (listSummary ? listSummary.count : 0);
    const listTotalCount = listSummary ? Math.max(listSummary.count, sortedInvoices.length) : sortedInvoices.length;

    const fetchMoreFromServer = async () => {
        setLoadingMore(true);
        try {
            const res = await axios.get(`${API}/invoices?${listQuery}&limit=200&skip=${todayInvoices.length}`, { headers: getAuthHeaders() });
            const seen = new Set(todayInvoices.map(i => i.id));
            setTodayInvoices([...todayInvoices, ...res.data.filter(i => !seen.has(i.id))]);
        } catch { /* keep what we have */ } finally { setLoadingMore(false); }
    };

    const loadMore = () => {
        if (visibleCount + ITEMS_PER_PAGE > todayInvoices.length && todayInvoices.length < listTotalCount && !loadingMore) fetchMoreFromServer();
        setVisibleCount(prev => prev + ITEMS_PER_PAGE);
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Pending Invoices Alert for Regular Users */}
            {user?.role === 'usuario' && (
                <PendingInvoicesAlert 
                    pendingCount={userPendingInvoices.length}
                    pendingInvoices={userPendingInvoices}
                />
            )}

            {/* Unprocessed Stripe/PayPal emails alert - Admin/Supervisor */}
            {(user?.role === 'admin' || user?.role === 'supervisor') && <UnprocessedEmailsAlert />}

            {/* Review Alert Banner - Admin only */}
            {user?.role === 'admin' && pendingReviewInvoices.length > 0 && (
                <div data-testid="review-alert-banner" className="bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="bg-red-500 rounded-full p-2">
                            <AlertTriangle className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-red-800 text-lg">
                                {pendingReviewInvoices.length} Factura{pendingReviewInvoices.length > 1 ? 's' : ''} en Revisión
                            </h3>
                            <p className="text-red-600 text-sm">Posibles duplicados detectados - requieren su aprobación</p>
                        </div>
                    </div>
                    <div className="space-y-2 mb-3">
                        {pendingReviewInvoices.slice(0, 5).map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-red-200">
                                <div>
                                    <span className="font-semibold text-red-900">{inv.name}</span>
                                    <span className="text-red-500 text-sm ml-2">({inv.user_name})</span>
                                </div>
                                <span className="font-bold text-red-700">${inv.amount?.toFixed(2)}</span>
                            </div>
                        ))}
                        {pendingReviewInvoices.length > 5 && (
                            <p className="text-xs text-red-500 text-center">y {pendingReviewInvoices.length - 5} más...</p>
                        )}
                    </div>
                    <Button 
                        size="sm"
                        onClick={() => navigate('/dashboard/invoices')}
                        className="w-full bg-red-500 hover:bg-red-600 text-white"
                        data-testid="review-go-to-invoices"
                    >
                        <Eye className="w-4 h-4 mr-2" />
                        Revisar Facturas
                    </Button>
                </div>
            )}

            {/* Welcome Section */}
            <div>
                <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
                    Bienvenido, {user?.name}
                </h1>
                <p className="text-slate-500 mt-1">Resumen de tu actividad en el sistema</p>
            </div>

            {/* Filters Section */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <DateFilter 
                            date={dateFrom} 
                            setDate={setDateFrom} 
                            placeholder="Desde" 
                            testId="dashboard-date-from-btn" 
                        />
                        <DateFilter 
                            date={dateTo} 
                            setDate={setDateTo} 
                            placeholder="Hasta" 
                            testId="dashboard-date-to-btn" 
                        />
                        <SingleDayFilter
                            day={dateFrom && dateTo && dateFrom.getTime() === dateTo.getTime() ? dateFrom : null}
                            onSelectDay={selectSingleDay}
                            testId="dashboard-single-day-btn"
                        />
                        <QuickRangeButtons
                            onSelectRange={selectRange}
                            activeFrom={dateFrom}
                            activeTo={dateTo}
                            testIdPrefix="dashboard"
                        />
                        {canFilterByUser && (
                            <UserFilter 
                                users={users} 
                                selectedUserId={selectedUserId} 
                                setSelectedUserId={setSelectedUserId}
                                testId="dashboard-user-filter"
                            />
                        )}
                        <FilterActions 
                            onFilter={() => fetchFilteredData()}
                            onClear={clearFilters}
                            hasActiveFilters={hasActiveFilters}
                            filterTestId="dashboard-apply-filters-btn"
                            clearTestId="dashboard-clear-filters-btn"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Stats Grid */}
            <StatsGrid stats={stats} formatCurrency={formatCurrency} userRole={user?.role} />

            {/* Verification Stats Panel - Only for admin */}
            {user?.role === 'admin' && (
                <VerificationStatsPanel />
            )}

            {/* Parsing Stats Panel - Only for admin */}
            {user?.role === 'admin' && (
                <ParsingStatsPanel />
            )}

            {/* Performance History Panel - Only for admin */}
            {user?.role === 'admin' && (
                <PerformanceHistoryPanel />
            )}

            {/* Pending Review Section - Only for admin/supervisor */}
            {(user?.role === 'admin' || user?.role === 'supervisor') && pendingReviewInvoices.length > 0 && (
                <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-amber-700 flex items-center gap-2">
                            <span className="text-xl">⚠️</span>
                            Facturas Pendientes de Revisión
                            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-sm">
                                {pendingReviewInvoices.length}
                            </span>
                        </CardTitle>
                        <p className="text-sm text-amber-600">
                            Posibles duplicados detectados que requieren tu aprobación
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {pendingReviewInvoices.map((invoice) => (
                                <PendingReviewItem 
                                    key={invoice.id}
                                    invoice={invoice}
                                    onApprove={handleApproveReview}
                                    onDelete={handleDeletePendingReview}
                                    formatCurrency={formatCurrency}
                                    getAuthHeaders={getAuthHeaders}
                                />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Invoices Section */}
            <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-sky-500" />
                            {hasActiveFilters ? 'Facturas Filtradas' : 'Facturas del Día'}
                            {selectedUserId && selectedUserId !== 'all' && (
                                <span className="text-sm font-normal text-sky-600 bg-sky-50 px-2 py-0.5 rounded">
                                    {users.find(u => u.id === selectedUserId)?.name || 'Usuario'}
                                </span>
                            )}
                        </CardTitle>
                        <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                            {dateFrom && dateTo ? (
                                dateFrom.getTime() === dateTo.getTime() 
                                    ? format(dateFrom, "dd 'de' MMMM, yyyy", { locale: es })
                                    : `${format(dateFrom, "dd/MM/yyyy")} - ${format(dateTo, "dd/MM/yyyy")}`
                            ) : dateFrom ? (
                                `Desde ${format(dateFrom, "dd/MM/yyyy")}`
                            ) : dateTo ? (
                                `Hasta ${format(dateTo, "dd/MM/yyyy")}`
                            ) : (
                                format(new Date(), "dd 'de' MMMM, yyyy", { locale: es })
                            )}
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1" data-testid="dashboard-list-count">
                        {listTotalCount} factura{listTotalCount !== 1 ? 's' : ''} 
                        {hasActiveFilters ? ' encontrada' : ' registrada'}{listTotalCount !== 1 ? 's' : ''}
                        {!hasActiveFilters && ' hoy'}
                    </p>
                </CardHeader>
                <CardContent>
                    {todayInvoices.length > 0 ? (
                        <>
                            <SimpleInvoiceTable 
                                invoices={paginatedInvoices}
                                sortKey={sortKey}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <InvoiceSummary 
                                invoices={sortedInvoices} 
                                formatCurrency={formatCurrency}
                                userRole={user?.role}
                                summary={listSummary}
                            />
                            <LoadMoreButton 
                                hasMore={hasMoreInvoices}
                                visibleCount={Math.min(visibleCount, sortedInvoices.length)}
                                totalCount={listTotalCount}
                                onLoadMore={loadMore}
                                testId="load-more-dashboard"
                            />
                        </>
                    ) : (
                        <EmptyState 
                            icon={FileText}
                            title="No hay facturas registradas hoy"
                            subtitle="Las facturas subidas hoy aparecerán aquí"
                        />
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default function Dashboard() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isHome = location.pathname === '/dashboard';
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        if (isHome && user?.role === 'usuario') {
            navigate('/dashboard/upload', { replace: true });
        }
    }, [isHome, user, navigate]);

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    return (
        <div className="flex min-h-screen bg-slate-50">
            <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
            <RefreshButton />
            
            <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm shadow-sm px-4 py-3 flex items-center gap-3 border-b border-slate-100">
                <MobileMenuButton onClick={toggleSidebar} />
                <span className="text-lg font-bold text-sky-600 ml-2">Contabilidad Clara</span>
            </div>
            
            <main className="flex-1 p-4 md:p-8 overflow-y-auto pt-20 md:pt-8 pb-24 md:pb-8">
                <div className="max-w-7xl mx-auto">
                    {isHome ? <DashboardHome /> : <Outlet />}
                </div>
            </main>

            <BottomNav onMoreClick={toggleSidebar} />
        </div>
    );
}
